import { Controller } from '@hotwired/stimulus';

/**
 * Live totals - keeps every total that includes the running entry in step with the timer bar's clock: on each
 * `timer:tick` it adds the clock's seconds to the target's base (its seconds without the running entry, rendered
 * by the server), so the entry row, day and week totals, chart and task totals move with the clock, no polling.
 * Formats like time_tracker_duration: "1h 07m", rounded to the minute ("compact": "1h07m", blank at zero).
 * Values:  dailySeconds (Number, the daily target: a chart bar's 100%)
 * Targets: total (data-live-totals-base, -format "compact", -template with "%<total>s") | bar (data-live-totals-base)
 * @example <div data-controller="live-totals" data-action="timer:tick->live-totals#update" data-live-totals-daily-seconds-value="25200">
 *            <span data-live-totals-target="total" data-live-totals-base="3360">0h 56m</span></div>
 */
export default class extends Controller {
  static targets = ['total', 'bar'];
  static values = { dailySeconds: Number };

  update ({ detail: { seconds } }) {
    this.totalTargets.forEach((total) => {
      const { liveTotalsFormat: format, liveTotalsTemplate: template } = total.dataset;
      const duration = this.#format(this.#base(total) + seconds, format);
      const text = template ? template.replace('%<total>s', duration) : duration;
      if (total.textContent !== text) total.textContent = text;
    });
    this.barTargets.forEach((bar) => this.#resize(bar, this.#base(bar) + seconds));
  }

  #base (element) {
    return Number(element.dataset.liveTotalsBase) || 0;
  }

  #format (seconds, format) {
    if (format === 'compact' && seconds <= 0) return '';

    const minutes = Math.round(seconds / 60);
    const text = `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
    return format === 'compact' ? text.replace(' ', '') : text;
  }

  // The bar's height class in steps of 5% of the daily target, capped at 110% as the week partial does.
  #resize (bar, seconds) {
    const step = this.dailySecondsValue > 0 ? Math.min(Math.round((seconds / this.dailySecondsValue) * 20) * 5, 110) : 0;
    const height = `week-col__bar--h${step}`;
    if (!bar.classList.contains(height)) {
      bar.classList.remove(...[...bar.classList].filter((name) => /^week-col__bar--h\d+$/.test(name)));
      bar.classList.add(height);
    }
    bar.classList.toggle('week-col__bar--empty', seconds <= 0);
  }
}
